import { useState, useMemo, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { TELEGRAM_URL } from "../lib/config";
import { checkZip } from "../lib/zip";
import { copyToClipboard } from "../lib/utils-extra";
import { Send, Copy, Truck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function OrderModal({ open, onOpenChange, product }) {
    const [variantIdx, setVariantIdx] = useState(product?._initialVariantIdx ?? 0);
    const [zip, setZip] = useState("");
    const [note, setNote] = useState("");

    // Sync when modal re-opens with a different starting variant
    useEffect(() => {
        if (open) {
            setVariantIdx(product?._initialVariantIdx ?? 0);
        }
    }, [open, product?._initialVariantIdx, product?.id]);

    const variant = product?.variants?.[variantIdx];
    const zipCheck = useMemo(() => checkZip(zip), [zip]);

    const message = useMemo(() => {
        if (!product || !variant) return "";
        const lines = [
            `Hi High95 — I'd like to order:`,
            ``,
            `• ${product.name} (${variant.label}) — $${Number(variant.price).toFixed(0)}`,
        ];
        if (zip) {
            const status =
                zipCheck?.status === "local"
                    ? "Local delivery"
                    : "Zip";
            lines.push(`• ${status}: ${zip}`);
        }
        if (note) {
            lines.push(``, note);
        }
        return lines.join("\n");
    }, [product, variant, zip, note, zipCheck]);

    if (!product) return null;

    const handleOrder = async () => {
        const ok = await copyToClipboard(message);
        if (ok) {
            toast.success("Order copied! Paste it in Telegram if it doesn't auto-fill.", {
                duration: 4000,
            });
        } else {
            toast.info("Open Telegram and send your order.");
        }
        // https URL pre-fills text on mobile Telegram apps and Telegram Desktop;
        // on web, user has the message on clipboard from above.
        const encoded = encodeURIComponent(message);
        const httpsUrl = `${TELEGRAM_URL}?text=${encoded}`;
        window.open(httpsUrl, "_blank", "noopener");
    };

    const StatusIcon =
        zipCheck?.status === "local" ? Truck : AlertTriangle;
    const statusTone =
        zipCheck?.status === "local"
            ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
            : "text-highway-sunset border-highway-sunset/30 bg-highway-sunset/10";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="order-modal"
                className="bg-card border-border text-white max-w-md max-h-[90vh] overflow-y-auto"
            >
                <DialogHeader>
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-highway-red">
                        // Express Lane
                    </p>
                    <DialogTitle className="font-display text-2xl mt-1">
                        Order via Telegram
                    </DialogTitle>
                    <DialogDescription className="text-white/60 text-sm">
                        Pick your size, add a note, and we'll copy your order
                        to clipboard before opening Telegram.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 mt-3">
                    {/* Product summary */}
                    <div className="flex gap-3 p-3 rounded-xl bg-background/60 border border-border">
                        <img
                            src={product.image_url}
                            alt=""
                            className="w-16 h-16 rounded-lg object-cover"
                        />
                        <div className="flex-1 min-w-0">
                            <p className="font-display font-semibold text-white text-sm leading-tight truncate">
                                {product.name}
                            </p>
                            {product.tier && (
                                <p className="font-mono text-[10px] uppercase tracking-widest text-white/50 mt-0.5">
                                    {product.tier}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Size */}
                    <div>
                        <Label className="font-mono text-xs uppercase tracking-widest text-white/60">
                            Size
                        </Label>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {product.variants.map((v, i) => {
                                const active = i === variantIdx;
                                return (
                                    <button
                                        key={v.label}
                                        data-testid={`order-size-${i}`}
                                        onClick={() => setVariantIdx(i)}
                                        className={`px-3 py-2 rounded-full text-xs font-mono uppercase tracking-wider transition-all border flex items-center gap-2 ${
                                            active
                                                ? "bg-white text-black border-white"
                                                : "bg-transparent text-white/70 border-white/15 hover:border-white/40 hover:text-white"
                                        }`}
                                    >
                                        <span>{v.label}</span>
                                        <span
                                            className={
                                                active
                                                    ? "text-black/60"
                                                    : "text-white/40"
                                            }
                                        >
                                            ${Number(v.price).toFixed(0)}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Zip */}
                    <div>
                        <Label className="font-mono text-xs uppercase tracking-widest text-white/60">
                            Delivery zip <span className="opacity-50">(optional)</span>
                        </Label>
                        <Input
                            data-testid="order-zip-input"
                            value={zip}
                            onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                            placeholder="08234"
                            inputMode="numeric"
                            className="bg-background/60 border-border h-11 mt-2 font-mono"
                        />
                        {zipCheck && zipCheck.status !== "invalid" && (
                            <div
                                data-testid="zip-status"
                                className={`mt-2 flex items-start gap-2 px-3 py-2 rounded-lg border text-xs ${statusTone}`}
                            >
                                <StatusIcon className="w-4 h-4 shrink-0 mt-0.5" />
                                <span>{zipCheck.message}</span>
                            </div>
                        )}
                    </div>

                    {/* Note */}
                    <div>
                        <Label className="font-mono text-xs uppercase tracking-widest text-white/60">
                            Add a note <span className="opacity-50">(optional)</span>
                        </Label>
                        <Textarea
                            data-testid="order-note-input"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={2}
                            placeholder="Quantity, payment preference, etc."
                            className="bg-background/60 border-border mt-2"
                        />
                    </div>

                    {/* Preview */}
                    <div className="p-3 rounded-xl bg-background/40 border border-border">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">
                            Message preview
                        </p>
                        <pre
                            data-testid="order-preview"
                            className="text-xs text-white/80 whitespace-pre-wrap font-mono"
                        >
                            {message}
                        </pre>
                    </div>

                    <Button
                        data-testid="order-confirm-btn"
                        onClick={handleOrder}
                        disabled={zipCheck?.status === "out_of_area"}
                        className="w-full rounded-full bg-highway-telegram hover:bg-highway-telegram/90 text-white py-6 font-semibold text-base disabled:opacity-50"
                    >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy & open Telegram
                        <Send className="w-4 h-4 ml-2" />
                    </Button>
                    <p className="text-[11px] text-white/40 text-center -mt-1">
                        We'll copy your order to clipboard — paste it as your first message in Telegram.
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    );
}
